import { formatCurrency } from '@/utils/money'

// How a widget's figure is written on screen (W7 tail, T2).
//
// The tile (WidgetPos_11_12_13_14) and the list widget (WidgetType_Table) each carried their
// own getToShow. The list widget's knew Currency, Decimal and Number; the tile's knew Currency
// and nothing else, so every other valueType - Number included - fell through and printed the
// raw figure, which is how a dashboard came to read 156265026.36. Rather than copy the working
// one a third time, both renderers now call this.
//
// A widget saved with no "Shown as" reads as a Number. The builder leaves valueType null until
// it is chosen, and the backend now stores Number for an absent one; this is the same rule on
// the way out, so a row already saved as null still reads right.
//
// The numeric formatting only ever applies to a value that is actually a figure. A List or
// Graph widget's cells, a quote number, and "No data" come back exactly as they arrived.

export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

// A plain figure, as the backend sends one. A value already carrying separators is left alone -
// it is already formatted - and so is anything with other characters in it.
const FIGURE = /^-?\d+(\.\d+)?$/

function isFigure(value) {
  if (typeof value === 'number') return Number.isFinite(value)
  return typeof value === 'string' && FIGURE.test(value.trim())
}

// The whole part, with thousands separators, unless it already has them.
function withSeparators(parts) {
  if (parts[0] && parts[0].split(',').length < 2) {
    parts[0] = numberWithCommas(parts[0])
  }
  return parts
}

// Exactly two decimal places for a Decimal (not money): a single digit is padded, more than two
// are cut. Currency is rounded by formatCurrency instead.
function twoPlaces(fraction) {
  return fraction.length === 1 ? `${fraction}0` : fraction.substring(0, 2)
}

export function formatWidgetValue(data, valueType) {
  let b = data
  if (b === null || b === undefined) return b

  const type =
    valueType === null || valueType === undefined || String(valueType).trim() === ''
      ? 'Number'
      : valueType

  if (type === 'Currency') {
    // Rounded to the penny by the shared helper (D3); a figure that already arrived with its sign
    // keeps one sign, not two.
    return formatCurrency(b)
  }

  if (!isFigure(b)) return b

  const c = withSeparators(b.toString().split('.'))

  if (type === 'Decimal') {
    return c[1] ? `${c[0]}.${twoPlaces(c[1])}` : `${c[0]}.00`
  }

  // Number, and anything unrecognised: the whole figure, no decimal part.
  return `${c[0]}`
}
